import {useState} from "react";
import {MS_DAY, utcMsToDate} from "../tools/CommonJS";
import {appCtx, isAWS, msgBox, useFetchDataCb, useFetchDataSingleton} from "./AppContext";
import {Button, FormLabel, TextInput, useInputChange} from "../tools/Inputs";
import {AccordionDiv, DashCard, ListItemValue} from "./CommonUIComponenets";
import {readFile, saveFile, selectFile} from "../tools/CommonHtml";
import {Dialog} from "../tools/UIDialog";

const base64regex = /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/;

export function useLicense() {
    const {rsp: license, refresh} = useFetchDataSingleton({
        api: "/api/v2/license_get",
        alt: {"data":[{"startDate":"2024-01-01T00:00:00Z","endDate":"2024-10-06T00:00:00Z","devices":{"":{"modules":["miscon"],"devices":20000,"activeDevices":16949}}}]}
    });

    return {license, refreshLicense: _ => refresh()};
}

export function getLicenseCurDate(license) {
    const now = Date.now();
    return license?.data?.find(l => new Date(l.startDate) <= now && new Date(l.endDate) > now);
}

export function CheckLicense({ children }) {
    const {license} = useLicense();
    return !license || getLicenseCurDate(license) ? children : <LicenseStatus noMargin/>;
}


export default function LicenseStatus({noMargin}) {
    const [newOrg, setNewOrg] = useState("");
    const [licIndex, setLicIndex] = useState(0);
    const {license, refreshLicense} = useLicense();
    const {fetchData} = useFetchDataCb();
    const curLicense = getLicenseCurDate(license);

    function onGenId() {
        appCtx.setDlg({Elm: GenerateIdDialog, company: license?.company, setNewOrg});
    }

    function uploadLicense() {
        selectFile(file =>
            readFile(file, licenseKey => {
                if (licenseKey.length < 300 || licenseKey.length > 2000 || !base64regex.test(licenseKey))
                    return msgBox("File contents are not a valid base64 key. Please verify that the file contains a single line of alphanumeric and punctuation characters.");
                fetchData({api: "/api/v2/license_upload", params: {licenseKey}, onData: refreshLicense,
                    setMsg: msg => msgBox(<>{msg}<div>Please verify that the correct license file with extension '.key' was selected.</div></>)});
            })
        );
    }

    return !license? null :
        <div className={`w-full flex flex-col gap-8 ${noMargin && "justify-center pt-28"}`}>
            <DashCard className={`p-card flex flex-col gap-4 ${noMargin? "w-[45rem]" : "w-full"}`}>
                {noMargin && <div className="text-xl font-semibold">License Status</div>}
                {!curLicense && <div className="text-sec text-base">
                    {!!license.data.length && "License has expired! "}{!isAWS && "Please upload a license key file."}
                </div>}
                {license.data.map((lic, idx) =>
                    <AccordionDiv
                        className="bg-bg3 px-4 py-2" handle
                        key={lic.startDate} isExpanded={licIndex === idx} onSetExpanded={e => setLicIndex(i => i === idx? -1 : idx)} arrow
                        summary={
                            <div className="flex gap-6">
                                <ListItemValue h="Start" val={lic.startDate && utcMsToDate(new Date(lic.startDate).getTime() || 0)}/>
                                <ListItemValue h="End" val={lic.endDate && utcMsToDate((new Date(lic.endDate).getTime() || 0) - MS_DAY)}/>
                            </div>
                        }
                    >
                        {Object.entries(lic.devices).map(([dev, devLic]) =>
                            <div key={dev} className="pl-4">
                                <div>{dev || "All"}:</div>
                                <div className="pl-4">Modules: {devLic.modules.join(", ")}</div>
                                <div className="pl-4">Device Limit: {devLic.devices.toLocaleString()}  (utilizing: {devLic.activeDevices.toLocaleString()})</div>
                            </div>
                        )}
                    </AccordionDiv>
                )}
            </DashCard>
            {!isAWS && <DashCard className={`p-card flex flex-col gap-4`}>
                <div>
                    Step-1:<br/>
                    <Button onClick={onGenId} className="inline-flex pr-1.5 text-prm-light">Generate ID:</Button>
                    if this a new server installation, or specifically requested by GYTPOL, this step will request you to enter some information, and then it will generate a license-id file which you should forward by email to
                    <MailtoGytpolAnchor org={newOrg || license?.company?.org || ""}/>
                </div>
                <div>
                    Step-2:<br/>
                    <Button onClick={uploadLicense} className="inline-flex pr-1.5 text-prm-light">Upload License:</Button>
                    upload the  file.key which you have received from GYTPOL.
                </div>
            </DashCard>}
        </div>;
}

const GenIdDialog_fields = [
    {f: "org", h: "Organization", p: "(name)", t: "text"},
    {f: "person", h: "Contact Person", p: "(name)", t: "text"},
    {f: "email", h: "Email", p: "(email address)", t: "email"},
];

function GenerateIdDialog({onClose, company, setNewOrg}) {
    const {inp, onChange} = useInputChange({...company});
    const {fetchData} = useFetchDataCb();

    function checkField(v) {
        if (!v)
            return `Input required.`;
    }

    function checkSave() {
        const {org, person, email} = inp;
        return [org, person, email].findPred(checkField);
    }

    function onGenId(e) {
        const {org} = inp;

        fetchData({
            api: "/api/v2/license_gen-id",
            params: inp,
            onData: r => {
                setNewOrg(org);
                saveFile((org && org.replace(/ /g, "_") || "") + "_license.id", {content: r.licenseId});
                onClose();
            }
        });
    }

    return (
        <Dialog
            {...{onClose}} className="dlg-x xl:min-w-[35rem]"
            title="Generate License ID"
            main={<div className="flex flex-col gap-3.5 text-sm">
                {GenIdDialog_fields.map(e =>
                    <FormLabel key={e.f} label={e.h} classNameCol1="w-36" classNameCol2="w-full">
                        <TextInput field={e.f} type={e.t} value={inp[e.f]} {...{onChange}} validate={checkField}/>
                    </FormLabel>)}
            </div>}
            buttons={<>
                <Button h="Cancel" className="px-6 py-2 text-base text-prm-main" onClick={onClose}/>
                <Button h="Generate" className="px-10 py-2 text-base text-prm-contrast bg-blue-500" onClick={onGenId} disabled={checkSave()}/>
            </>}
        />
    );
}


function MailtoGytpolAnchor({org}) {
    return (
        <a className="text-orange-600 italic ml-1"
           href={`mailto:license@gytpol.com?subject=${escape(`License request for ${org || ""}`)}&body=${escape("Hi. Attaching our license.id...")}`}
           target="_blank"
        >
            license@gytpol.com
        </a>
    );
}
